import React, { useState, useEffect } from "react";
import { Link as RouterLink, useLocation, useMatch } from "react-router-dom";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListSubheader,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import structure from "./structure";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

const NavMenu = () => {
  const [expanded, setExpanded] = useState([]);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!pathname) return;
    const match = structure.find((s) => pathname.startsWith(`/${s.path}/`));
    if (match) {
      setExpanded((ex) => (ex.includes(match.path) ? ex : [...ex, match.path]));
    }
  }, [pathname]);

  return (
    <Box
      sx={{
        width: "300px",
        minWidth: "300px",
        height: "100%",
        overflowY: "auto",
        paddingBottom: 2,
        backgroundColor: "background.white",
        borderRightStyle: "solid",
        borderRightWidth: 1,
        borderRightColor: "grey.300",
        ".MuiAccordionSummary-root": {
          transition: "none",
        },
        ".MuiAccordionSummary-root.Mui-expanded": {
          backgroundColor: "primary.main",
          color: "primary.contrastText",
        },
        ".MuiAccordionSummary-root:hover, .MuiAccordionSummary-root.Mui-expanded:hover":
          {
            backgroundColor: "primary.dark",
            color: "primary.contrastText",
          },
        ".MuiAccordionDetails-root": {
          p: 0,
          backgroundColor: "secondary.light",
          color: "secondary.contrastText",
        },
        ".MuiAccordionDetails-root > .MuiList-root": {
          py: 2,
        },
        ".MuiListSubheader-root": {
          position: "relative",
          lineHeight: "1.2em",
          fontSize: "0.8rem",
          pt: 0.5,
          pb: 0.5,
          px: 4,
          opacity: 0.7,
          backgroundColor: "transparent",
        },
        ".MuiListItemButton-root + .MuiListSubheader-root": {
          pt: 2,
        },
        ".MuiListItemButton-root": {
          py: 0.5,
          pl: 0.5,
          pr: 4,
        },
        ".MuiListItemButton-root.Mui-selected": {
          backgroundColor: "secondary.main",
        },
        ".MuiListItemButton-root:hover, .MuiListItemButton-root.Mui-selected:hover":
          {
            backgroundColor: "secondary.dark",
          },
        ".MuiListItemIcon-root": {
          minWidth: 28,
        },
        ".MuiListItemButton-root:not(.Mui-selected) .MuiListItemIcon-root": {
          opacity: 0,
        },
        ".MuiListItemText-root": {
          margin: 0,
        },
      }}
    >
      {structure.map((s) => (
        <Accordion
          disableGutters
          key={s.path}
          expanded={expanded.includes(s.path)}
          onChange={(e, expanded) =>
            setExpanded((ex) =>
              expanded
                ? ex.includes(s.path)
                  ? ex
                  : [...ex, s.path]
                : ex.filter((x) => x !== s.path)
            )
          }
        >
          <AccordionSummary>{s.name}</AccordionSummary>
          <AccordionDetails>
            <List>
              {s.items.map((i) => (
                <React.Fragment key={i.path}>
                  {i.component && (
                    <NavMenuLink to={`/${s.path}/${i.path}`}>
                      {i.name}
                    </NavMenuLink>
                  )}
                  {i.group && (
                    <React.Fragment>
                      <ListSubheader>{i.name}</ListSubheader>
                      {i.group.map((g) => (
                        <NavMenuLink
                          key={g.path}
                          to={`/${s.path}/${i.path}/${g.path}`}
                        >
                          {g.name}
                        </NavMenuLink>
                      ))}
                    </React.Fragment>
                  )}
                </React.Fragment>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

const NavMenuLink = ({ to, children }) => {
  const match = useMatch(to);
  return (
    <ListItemButton
      component={RouterLink}
      to={to}
      selected={!!match}
      disableRipple
    >
      <ListItemIcon>
        <KeyboardArrowRightIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText primaryTypographyProps={{ variant: "body2" }}>
        {children}
      </ListItemText>
    </ListItemButton>
  );
};

export default NavMenu;
